import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { errorReporter, isFirebaseOfflineError } from '@/services/errorReportingService';

const useRealTimeProcessingType = (id?: string | null, processingType?: string | null | undefined) => {
  // Initialize based on whether processingType is already set
  const [isProcessing, setIsProcessing] = useState<boolean>(() => {
    // If processingType is not null, we're not processing
    if (processingType !== null && processingType !== undefined) {
      return false;
    }
    // Otherwise, assume we're processing until Firebase tells us otherwise
    return true;
  });

  useEffect(() => {
    if (!id) return;

    const hasResolvedType = processingType !== null && processingType !== undefined;

    // Re-derive on every note change. The useState initializer above runs ONCE,
    // but the Note page stays mounted across noteId changes — so without this the
    // previous note's flag persisted until the Firestore snapshot landed, and a
    // finished note briefly rendered "The video is processing."
    setIsProcessing(!hasResolvedType);

    // If processingType is already set (not null), we're done processing
    if (hasResolvedType) {
      return;
    }

    const docRef = doc(collection(db, 'notes'), id);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const newProcessingType = docSnapshot.data()?.processing_type || null;
          setIsProcessing(newProcessingType === null);
        } else {
          // No document found
          setIsProcessing(false);
        }
      },
      (error) => {
        if (isFirebaseOfflineError(error)) {
          errorReporter.expected('Processing type snapshot failed (client offline)', error, {
            feature: 'firebase',
            operation: 'realTimeProcessingType',
          });
        } else {
          errorReporter.critical('Failed to fetch processing type snapshot', error, {
            feature: 'notes',
            operation: 'realTimeProcessingType',
            noteId: id,
          });
        }
      },
    );

    return () => unsubscribe();
  }, [id, processingType]);

  return isProcessing;
};

export default useRealTimeProcessingType;
